import { Fragment, useEffect, useMemo, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import swal from "sweetalert";
import api, { getFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";
import ReceiptActions from "../Common/ReceiptActions";
import SignedBillActions from "../Common/SignedBillActions";

const money = (value) =>
 new Intl.NumberFormat("en-IN", {
 style: "currency",
 currency: "INR",
 }).format(Number(value || 0));

const getPaymentRows = (record) => {
 const payments = Array.isArray(record?.payments) ? [...record.payments] : [];
 const paidTotal = Number(record?.paidAmount || 0);
 const recordedTotal = payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
 const legacyAmount = paidTotal - recordedTotal;
 if (legacyAmount > 0.005) {
 payments.unshift({
 _id: `legacy-${record._id}`,
 amount: legacyAmount,
 paymentReference: record.paymentReference,
 paymentRemarks: record.paymentRemarks,
 paymentDate: record.paidDate || record.paymentDate || record.updatedAt,
 receipt: record.receipt,
 legacy: true,
 });
 }
 return payments;
};

const statusVariant = (status) => {
 if (status === "Paid") return "success";
 if (status === "Partially Paid") return "warning";
 if (status === "Rejected") return "danger";
 return "info";
};

const VendorPaymentList = () => {
 const [bills, setBills] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [projects, setProjects] = useState([]);
 const [searchParams] = useSearchParams();
 const workOrderFromUrl = searchParams.get("workOrder") || "";
 const projectFromUrl = searchParams.get("project") || "";
 const vendorWorkOrderFromUrl = searchParams.get("vendorWorkOrder") || "";
 const [selectedProject, setSelectedProject] = usePersistentState("vendor_pay_project", projectFromUrl);
 const [selectedWorkOrder, setSelectedWorkOrder] = usePersistentState("vendor_pay_wo", workOrderFromUrl);
 const [searchQuery, setSearchQuery] = usePersistentState("vendor_pay_search", "");
 const [statusFilter, setStatusFilter] = usePersistentState("vendor_pay_status", "All");
  const [currentPage, setCurrentPage] = usePersistentState("vendor_pay_page", 1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, selectedProject, selectedWorkOrder, vendorWorkOrderFromUrl]);
 const [loading, setLoading] = useState(true);
 const [selectedBill, setSelectedBill] = useState(null);
 const [paidAmount, setPaidAmount] = useState("");
 const [paymentReference, setPaymentReference] = useState("");
 const [paymentRemarks, setPaymentRemarks] = useState("");
 const [saving, setSaving] = useState(false);
 const [downloadingPdf, setDownloadingPdf] = useState(null);
 const [previewingPdf, setPreviewingPdf] = useState(null);

 const paymentPreview = selectedBill
 ? (() => {
 const workOrderTotal = Number(selectedBill.vendorWorkOrder?.totalAmount || 0);
 const billTotal = Number(selectedBill.totalAmount || 0);
 const currentPaid = Number(selectedBill.paidAmount || 0);
 const nextPaid = currentPaid + Number(paidAmount || 0);
 const remaining = Math.max(billTotal - currentPaid, 0);
 const nextRemaining = Math.max(billTotal - nextPaid, 0);
 const nextStatus =
 nextPaid >= billTotal
 ? "Paid"
 : nextPaid > 0
 ? "Partially Paid"
 : "Submitted";

 return {
 workOrderTotal,
 billTotal,
 currentPaid,
 nextPaid,
 remaining,
 nextRemaining,
 nextStatus,
 };
 })()
 : null;

 useEffect(() => {
 const fetchDropdownData = async () => {
 try {
 const [woRes, projRes] = await Promise.all([
 api.get("/work-orders?limit=1000"),
 api.get("/projects?limit=1000"),
 ]);
 if (woRes.data?.success) setWorkOrders(woRes.data.data || []);
 if (projRes.data?.success) setProjects(projRes.data.data || []);
 } catch (err) {
 console.error("Failed to fetch dropdown data", err);
 }
 };
 fetchDropdownData();
 }, []);

 const availableWorkOrders = useMemo(() => {
 if (!selectedProject) return workOrders;
 return workOrders.filter((wo) => {
 const pId = wo.project?._id || (typeof wo.project === "string" ? wo.project : null);
 return String(pId) === String(selectedProject);
 });
 }, [workOrders, selectedProject]);

 useEffect(() => {
 if (workOrderFromUrl) {
 setSelectedWorkOrder(workOrderFromUrl);
 }
 }, [workOrderFromUrl]);

 const loadBills = async () => {
 try {
 setLoading(true);
 const params = new URLSearchParams({ limit: "1000" });
 if (selectedWorkOrder) params.append("workOrder", selectedWorkOrder);
 if (vendorWorkOrderFromUrl) params.append("vendorWorkOrder", vendorWorkOrderFromUrl);

 const billRes = await api.get(`/vendor-work-order-billings?${params.toString()}`);

 if (billRes.data.success) setBills(billRes.data.data || []);
 } catch (error) {
 swal("Error", "Failed to load vendor payments", "error");
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 loadBills();
 }, [selectedWorkOrder, vendorWorkOrderFromUrl]);

 const filteredBills = useMemo(() => {
 return bills.filter((b) => {
 if (selectedProject) {
 const bWoId = String(b.workOrder?._id || b.workOrder || "");
 const matchingWo = workOrders.find((w) => String(w._id) === bWoId);
 const projId = b.project?._id || (typeof b.project === "string" ? b.project : null)
 || matchingWo?.project?._id || (typeof matchingWo?.project === "string" ? matchingWo.project : null);
 if (String(projId) !== String(selectedProject)) {
 return false;
 }
 }
 if (statusFilter !== "All" && b.status !== statusFilter) {
 return false;
 }
 if (searchQuery.trim()) {
 const q = searchQuery.toLowerCase().trim();
 const bNo = (b.billNo || "").toLowerCase();
 const invNo = (b.invoiceNo || "").toLowerCase();
 const vwoNo = (b.vendorWorkOrder?.woNumber || "").toLowerCase();
 const title = (b.vendorWorkOrder?.title || "").toLowerCase();
 const vName = (b.vendor?.name || "").toLowerCase();
 const ref = (b.paymentReference || "").toLowerCase();
 if (
 !bNo.includes(q) &&
 !invNo.includes(q) &&
 !vwoNo.includes(q) &&
 !title.includes(q) &&
 !vName.includes(q) &&
 !ref.includes(q)
 ) {
 return false;
 }
 }
 return true;
 });
 }, [bills, statusFilter, searchQuery, selectedProject, workOrders]);

  const totalCount = filteredBills.length;
  const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;
  const paginatedBills = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredBills.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredBills, currentPage, itemsPerPage]);

 const openPayment = (bill) => {
 setSelectedBill(bill);
 setPaidAmount(String(Math.max(Number(bill.totalAmount || 0) - Number(bill.paidAmount || 0), 0)));
 setPaymentReference(bill.paymentReference || "");
 setPaymentRemarks(bill.paymentRemarks || "");
 };

 const savePayment = async () => {
 if (!selectedBill) return;
 const total = Number(selectedBill.totalAmount || 0);
 const nextPaid = Number(paidAmount || 0);
 const currentPaid = Number(selectedBill.paidAmount || 0);
 if (nextPaid <= 0 || nextPaid > total - currentPaid) {
 return swal("Error", "Payment amount must be greater than 0 and cannot exceed the remaining amount", "error");
 }

 try {
 setSaving(true);
 const res = await api.patch(`/vendor-work-order-billings/${selectedBill._id}/payment`, {
 paidAmount: nextPaid,
 paymentReference,
 paymentRemarks,
 });
 if (res.data.success) {
 swal("Success", "Vendor payment recorded", "success");
 setSelectedBill(null);
 loadBills();
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to update payment", "error");
 } finally {
 setSaving(false);
 }
 };

 const previewBillPDF = async (billId) => {
 setPreviewingPdf(billId);
 const previewWindow = window.open("", "_blank");
 try {
 const res = await api.get(`/vendor-work-order-billings/${billId}/pdf`, {
 responseType: "blob",
 params: { mode: "preview" },
 });
 const blob = new Blob([res.data], { type: "application/pdf" });
 const url = window.URL.createObjectURL(blob);
 if (previewWindow) previewWindow.location.href = url;
 } catch (error) {
 swal("Error", "Failed to generate bill PDF", "error");
 previewWindow?.close();
 } finally {
 setPreviewingPdf(null);
 }
 };

 const downloadBillPDF = async (billId) => {
 setDownloadingPdf(billId);
 try {
 const res = await api.get(`/vendor-work-order-billings/${billId}/pdf`, {
 responseType: "blob",
 timeout: 120000,
 });
 const blob = new Blob([res.data], { type: "application/pdf" });
 const url = window.URL.createObjectURL(blob);
 const link = document.createElement("a");
 link.href = url;
 link.download = `vendor-bill-${billId}.pdf`;
 link.click();
 window.URL.revokeObjectURL(url);
 } catch (error) {
 swal("Error", "Failed to download bill PDF", "error");
 } finally {
 setDownloadingPdf(null);
 }
 };

 return (
 <Fragment>
 <PageTitle activeMenu="Vendor Payments" motherMenu="Accounts" />
 {/* ===== FILTER & SEARCH BAR ===== */}
 <Card className="shadow-sm border-0 mb-4">
 <Card.Body>
 <Row className="g-3 align-items-center">
 <Col md={3}>
 <Form.Label className="small fw-semibold mb-1">Search</Form.Label>
 <Form.Control
 type="text"
 placeholder="Search Bill, Vendor WO, Title..."
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 />
 </Col>
 <Col md={3}>
 <Form.Label className="small fw-semibold mb-1">Project</Form.Label>
 <Form.Select
 value={selectedProject}
 onChange={(e) => {
 setSelectedProject(e.target.value);
 setSelectedWorkOrder("");
 }}
 >
 <option value="">All Projects</option>
 {projects.map((p) => (
 <option key={p._id} value={p._id}>
 {p.projectName || p.name || p.projectCode || p._id}
 </option>
 ))}
 </Form.Select>
 </Col>
 <Col md={3}>
 <Form.Label className="small fw-semibold mb-1">Work Order</Form.Label>
 <Form.Select
 value={selectedWorkOrder}
 onChange={(e) => setSelectedWorkOrder(e.target.value)}
 >
 <option value="">All Work Orders</option>
 {availableWorkOrders.map((wo) => (
 <option key={wo._id} value={wo._id}>
 {wo.woNo || wo.woNumber}{wo.label ? ` – ${wo.label}` : ""}
 </option>
 ))}
 </Form.Select>
 </Col>
 <Col md={2}>
 <Form.Label className="small fw-semibold mb-1">Payment Status</Form.Label>
 <Form.Select
 value={statusFilter}
 onChange={(e) => setStatusFilter(e.target.value)}
 >
 <option value="All">All Statuses</option>
 <option value="Paid">Paid</option>
 <option value="Partially Paid">Partially Paid</option>
 <option value="Submitted">Submitted / Pending</option>
 <option value="Rejected">Rejected</option>
 </Form.Select>
 </Col>
 <Col md={1} className="d-flex align-items-end">
 <Button
 variant="outline-secondary"
 className="w-100 mt-4 d-flex align-items-center justify-content-center"
 style={{ height: "38px" }}
 title="Reset Filters"
 onClick={() => {
 setSearchQuery("");
 setStatusFilter("All");
 setSelectedProject("");
 setSelectedWorkOrder("");
 }}
 >
 <i className="fas fa-undo" />
 </Button>
 </Col>
 </Row>
 </Card.Body>
 </Card>

 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">
 Vendor Payment Review
 {selectedWorkOrder && (
 <Badge bg="secondary" className="ms-2">
 Work Order Filtered
 </Badge>
 )}
 {vendorWorkOrderFromUrl && (
 <Badge bg="info" className="ms-2">
 Vendor WO Filtered
 </Badge>
 )}
 </Card.Title>
 <span className="text-muted small">
 Showing <strong>{filteredBills.length}</strong> of <strong>{bills.length}</strong> records
 </span>
 </Card.Header>
 <Card.Body>
 {loading ? (
 <div className="text-center py-5">
 <Spinner />
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="align-middle">
 <thead>
 <tr>
 <th>Bill No</th>
 <th>Vendor WO</th>
 <th>Title</th>
 <th>Vendor</th>
 <th>Total</th>
 <th>Paid</th>
 <th>Status</th>
 <th>Bill Attachment</th>
 <th>Receipt</th>
 <th>Signed Bill</th>
 <th>View Bill</th>
 <th className="text-end text-nowrap" style={{ minWidth: "140px" }}>Action</th>
 </tr>
 </thead>
 <tbody>
 {filteredBills.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center text-muted py-5">
                    No vendor payment records found matching the filter criteria.
                  </td>
                </tr>
              ) : (
                paginatedBills.map((bill) => (
 <tr key={bill._id}>
 <td>
 <strong>{bill.billNo}</strong>
 <div className="small text-muted">{bill.invoiceNo || ""}</div>
 </td>
 <td>{bill.vendorWorkOrder?.woNumber || "-"}</td>
 <td>{bill.vendorWorkOrder?.title || "-"}</td>
 <td>{bill.vendor?.name || "-"}</td>
 <td>{money(bill.vendorWorkOrder?.totalAmount || bill.totalAmount)}</td>
 <td>{money(bill.paidAmount)}</td>
 <td>
 <Badge bg={statusVariant(bill.status)}>{bill.status}</Badge>
 </td>
 <td>
 {bill.attachment?.fileUrl ? (
 <a
 href={getFileUrl(bill.attachment.fileUrl)}
 target="_blank"
 rel="noreferrer"
 className="btn btn-outline-secondary btn-sm"
 >
 <i className="fa fa-paperclip me-2"></i>
 {bill.attachment.fileName || "Download"}
 </a>
 ) : (
 <span className="text-muted">-</span>
 )}
 </td>
 <td>
 <div className="d-flex flex-column gap-1">
 {getPaymentRows(bill).map((payment, index) => (
 <div key={payment._id || index} className="d-flex align-items-center gap-1">
 <span className="small text-muted text-nowrap">P{index + 1} {money(payment.amount)}</span>
 <ReceiptActions id={bill._id} type="vendor-work-order-billings" receipt={payment.receipt} query={payment.legacy ? "" : `?paymentId=${payment._id}`} canUpload={true} onUploadSuccess={loadBills} />
 </div>
 ))}
 {!getPaymentRows(bill).length && <span className="text-muted">-</span>}
 </div>
 </td>
 <td>
 <SignedBillActions
 id={bill._id}
 type="vendor-work-order-billings"
 signedCopy={bill.signedCopy}
 canUpload={false}
 />
 </td>
 <td>
 <div className="d-flex gap-1">
 <Button
 size="sm"
 variant="outline-primary"
 className="px-2 py-1"
 title="Preview PDF"
 onClick={() => previewBillPDF(bill._id)}
 disabled={previewingPdf === bill._id || downloadingPdf === bill._id}
 >
 {previewingPdf === bill._id ? (
 <Spinner size="sm" style={{ width: "1rem", height: "1rem" }} />
 ) : (
 <i className="fa fa-eye"></i>
 )}
 </Button>
 <Button
 size="sm"
 variant="outline-success"
 className="px-2 py-1"
 title="Download PDF"
 onClick={() => downloadBillPDF(bill._id)}
 disabled={previewingPdf === bill._id || downloadingPdf === bill._id}
 >
 {downloadingPdf === bill._id ? (
 <Spinner size="sm" style={{ width: "1rem", height: "1rem" }} />
 ) : (
 <i className="fa fa-download"></i>
 )}
 </Button>
 </div>
 </td>
 <td className="text-end align-middle" style={{ minWidth: "140px" }}>
 <Button
 size="sm"
 variant="outline-primary"
 className="text-nowrap"
 onClick={() => openPayment(bill)}
 >
 Review / Mark Paid
 </Button>
 </td>
 </tr>
                )))}
 </tbody>
 </Table>
 </div>
 )}
 {!loading && filteredBills.length > 0 && (
            <AppPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              itemsLength={paginatedBills.length}
              limit={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          )}
          </Card.Body>
 </Card>

 <Modal show={!!selectedBill} onHide={() => setSelectedBill(null)} centered scrollable>
 <Modal.Header closeButton>
 <Modal.Title>Vendor Payment</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {selectedBill && (
 <>
 <div className="mb-3">
 <div>
 <strong>Bill:</strong> {selectedBill.billNo}
 </div>
 <div>
 <strong>Vendor WO:</strong>{" "}
 {selectedBill.vendorWorkOrder?.woNumber || "-"}
 </div>
 <div>
 <strong>Vendor:</strong> {selectedBill.vendor?.name || "-"}
 </div>
 </div>
 {paymentPreview && (
 <div className="border rounded p-3 mb-3 bg-light">
 <div className="d-flex justify-content-between mb-1">
 <span>WO Total Amount</span>
 <strong>{money(paymentPreview.workOrderTotal)}</strong>
 </div>
 <div className="d-flex justify-content-between mb-1">
 <span>Bill Total Amount</span>
 <strong>{money(paymentPreview.billTotal)}</strong>
 </div>
 <div className="d-flex justify-content-between mb-1">
 <span>Already Paid</span>
 <strong className="text-success">{money(paymentPreview.currentPaid)}</strong>
 </div>
 <div className="d-flex justify-content-between mb-1">
 <span>Current Remaining</span>
 <strong className="text-danger">{money(paymentPreview.remaining)}</strong>
 </div>
 <hr className="my-2" />
 <div className="d-flex justify-content-between mb-1">
 <span>Paid After Save</span>
 <strong>{money(paymentPreview.nextPaid)}</strong>
 </div>
 <div className="d-flex justify-content-between mb-1">
 <span>Remaining After Save</span>
 <strong className={paymentPreview.nextRemaining > 0 ? "text-warning" : "text-success"}>
 {money(paymentPreview.nextRemaining)}
 </strong>
 </div>
 <div className="d-flex justify-content-between align-items-center">
 <span>Status After Save</span>
 <Badge bg={statusVariant(paymentPreview.nextStatus)}>
 {paymentPreview.nextStatus}
 </Badge>
 </div>
 </div>
 )}
 <div className="border rounded p-3 mb-3 bg-light small">
 <i className="fas fa-info-circle text-primary me-2"></i>
 Save this payment first. Its separate receipt upload button will appear beside the new installment in the payment list.
 </div>
 <div className="border rounded p-3 mb-3">
 <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
 <div>
 <div className="text-muted small">Signed Bill</div>
 <div className="fw-semibold">
 {selectedBill.signedCopy?.uploadedAt
 ? new Date(selectedBill.signedCopy.uploadedAt).toLocaleDateString("en-IN")
 : "Not uploaded"}
 </div>
 </div>
 <SignedBillActions
 id={selectedBill._id}
 type="vendor-work-order-billings"
 signedCopy={selectedBill.signedCopy}
 canUpload={false}
 onUploadSuccess={loadBills}
 />
 </div>
 </div>
 <Form.Group className="mb-3">
 <Form.Label>This Payment Amount</Form.Label>
 <Form.Control
 type="number"
 min="0"
 max={paymentPreview?.remaining || 0}
 value={paidAmount}
 onChange={(e) => setPaidAmount(e.target.value)}
 />
 </Form.Group>
 <Form.Group className="mb-3">
 <Form.Label>Payment Reference</Form.Label>
 <Form.Control
 value={paymentReference}
 onChange={(e) => setPaymentReference(e.target.value)}
 />
 </Form.Group>
 <Form.Group>
 <Form.Label>Remarks</Form.Label>
 <Form.Control
 as="textarea"
 rows={3}
 value={paymentRemarks}
 onChange={(e) => setPaymentRemarks(e.target.value)}
 />
 </Form.Group>
 <div className="border rounded p-3 mt-3">
 <div className="fw-semibold mb-2">Payment History</div>
 {getPaymentRows(selectedBill).map((payment, index) => (
 <div key={payment._id || index} className="d-flex justify-content-between align-items-center border-top py-2 gap-2">
 <div>
 <div className="fw-semibold">{money(payment.amount)}</div>
 <div className="small text-muted">{payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString("en-IN") : "-"}{payment.paymentReference ? ` · ${payment.paymentReference}` : ""}</div>
 </div>
 <ReceiptActions id={selectedBill._id} type="vendor-work-order-billings" receipt={payment.receipt} query={payment.legacy ? "" : `?paymentId=${payment._id}`} onUploadSuccess={loadBills} />
 </div>
 ))}
 {!getPaymentRows(selectedBill).length && <div className="small text-muted">No payments recorded yet.</div>}
 </div>
 </>
 )}
 </Modal.Body>
 <Modal.Footer>
 <Button variant="light" onClick={() => setSelectedBill(null)}>
 Cancel
 </Button>
 <Button onClick={savePayment} disabled={saving}>
 {saving ? "Saving..." : "Save Payment"}
 </Button>
 </Modal.Footer>
 </Modal>
 </Fragment>
 );
};

export default VendorPaymentList;
